import { useState, useEffect } from 'react';
import { fetchActivityLog } from '@/api/activityLog';
import type { ActivityLogEntry } from '@/api/activityLog';

export function PlanningActivityLogView() {
  const [entries, setEntries] = useState<ActivityLogEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    fetchActivityLog(200, 'planning')
      .then(data => {
        setEntries(data.entries);
        setTotal(data.total);
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : 'Onbekende fout'))
      .finally(() => setLoading(false));
  }, []);

  // Group by date
  const grouped = new Map<string, ActivityLogEntry[]>();
  for (const entry of entries) {
    const date = new Date(entry.ts);
    const key = date.toLocaleDateString('nl-BE');
    const existing = grouped.get(key) ?? [];
    existing.push(entry);
    grouped.set(key, existing);
  }

  return (
    <div className="px-4 lg:px-8 py-6">
      <h1 className="text-xl font-bold text-td-navy mb-1">Planning Activiteitenlog</h1>
      <p className="text-sm text-td-muted mb-6">
        {total} acties totaal
      </p>

      {loading && (
        <div className="text-center py-12 text-td-muted text-sm">Laden...</div>
      )}

      {!loading && error && (
        <div className="mb-6 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          Kon activiteitenlog niet laden: {error}
        </div>
      )}

      {!loading && !error && entries.length === 0 && (
        <div className="text-center py-16">
          <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
          <p className="text-td-muted text-sm font-medium">Nog geen activiteiten</p>
        </div>
      )}

      {!error && [...grouped.entries()].map(([dateStr, dayEntries]) => {
        const firstDate = new Date(dayEntries[0].ts);
        const DUTCH_DAYS = ['zo', 'ma', 'di', 'wo', 'do', 'vr', 'za'];
        const DUTCH_MONTHS = ['jan', 'feb', 'mrt', 'apr', 'mei', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'];
        const label = `${DUTCH_DAYS[firstDate.getDay()]} ${firstDate.getDate()} ${DUTCH_MONTHS[firstDate.getMonth()]}`;

        return (
          <div key={dateStr} className="mb-6">
            <h2 className="text-xs font-bold text-td-muted uppercase tracking-wider mb-2">{label}</h2>
            <div className="bg-white border rounded-lg divide-y divide-gray-100">
              {dayEntries.map((entry, i) => {
                const time = new Date(entry.ts);
                return (
                  <div key={i} className="flex items-center gap-4 px-4 py-3">
                    <span className="text-sm text-td-light shrink-0 w-16 font-mono">
                      {time.toLocaleTimeString('nl-BE', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                    </span>
                    <div className="min-w-0 flex-1">
                      <span className="text-sm font-semibold text-td-navy">{entry.action}</span>
                      {entry.detail && <span className="text-sm text-td-muted ml-2">{entry.detail}</span>}
                    </div>
                    <span className="text-xs text-td-light shrink-0">{entry.userName}</span>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
